// 引入axios
import axios from 'axios'
import qs from 'qs'
import { v4 as uuidv4 } from 'uuid';
// import Vue from 'vue'
import { getToken, removeToken, getQueryParam, isMobile, shouldSkipIOSNativeBridge, isMiniAppWebview } from '@/utils/auth.ts'
import { ElMessage } from 'element-plus'
import { encryptRequestBody, decryptAES, cacheDecryptionKey, getDecryptionKey, clearDecryptionKey } from '@/utils/encrypt.js'
// import { useAppStore } from '@/store'
import { storeToRefs } from 'pinia'
import { getWebXSignHeader } from '@/utils/webXSign.js'

// 定义类型
interface AppInfo {
  version?: string;
  versionCode?: number;
  pkg?: string;
  platform?: string;
  di?: string;
  oi?: string;
  dn?: string;
  andrioInfo?: AppInfo;
}

interface ApiResponse {
  code: string;
  data?: any;
  msg?: string;
}




const baseURL = import.meta.env.VITE_BASE_URL + import.meta.env.VITE_API_URL
// console.log(import.meta.env.VITE_BASE_URL,import.meta.env.VITE_API_URL)
// console.log(baseURL,import.meta.env.PROD)
// const baseURL = ''
const fileURL = ''

// RSA公钥缓存
let rsaPublicKeyCache: string | null = null

/** 与 httpService POST 拦截器合并的公共字段一致（deviceType、di、version 等） */
async function buildCommonPostDataBase(requestTimestamp: number) {
  let version, versionCode, pkg, platform, di, oi, dn
  const defaultValues = {
    version: '',
    versionCode: '',
    pkg: '',
    platform: '',
    di: '',
    oi: '',
    dn: '',
  }
  try {
    const { useAppStore } = await import('@/store/app')
    const appStore = useAppStore()
    const parsedData: any = appStore.getAndrio
    if (parsedData) {
      ({ version, versionCode, pkg, platform, di, oi, dn } = {
        ...defaultValues,
        ...parsedData,
      })
    } else {
      ({ version, versionCode, pkg, platform, di, oi, dn } = defaultValues)
    }
  } catch {
    ({ version, versionCode, pkg, platform, di, oi, dn } = defaultValues)
  }

  return {
    deviceType: isMiniAppWebview() ? 'WECHAT_MINI' : (isMobile() ? platform : 'WEB'),
    version: isMiniAppWebview() ? '1.2.0' : (isMobile() ? version : '1.0.0'),
    versionInt: isMiniAppWebview() ? 120 : (isMobile() ? versionCode : '100'),
    pkg: isMiniAppWebview() ? 'ydppt_wechat' : (isMobile() ? pkg : 'ydppt_web'),
    di: isMobile() ? di : localStorage.getItem('device_id'),
    oi,
    dn,
    requestId: uuidv4(),
    timestamp: requestTimestamp,
  }
}

// 获取RSA公钥
async function getRSAPublicKey(deviceType: string): Promise<string> {
  if (rsaPublicKeyCache) {
    // console.log('使用缓存的RSA公钥:', rsaPublicKeyCache)
    return rsaPublicKeyCache
  }

  try {
    // console.log('开始获取RSA公钥...')
    const requestTimestamp = Date.now()
    const postData = {
      ...(await buildCommonPostDataBase(requestTimestamp)),
      deviceType,
    }
    const rawAxiosConfig: { headers: Record<string, string> } = { headers: {} }
    rawAxiosConfig.headers['sign-agent'] = 'ydppt'
    if (!isMobile()) {
      rawAxiosConfig.headers['X-Sign'] = getWebXSignHeader(requestTimestamp)
    }
    const response: any = await axios.post('/api/c/get', postData, rawAxiosConfig)
    // console.log('获取RSA公钥响应:', response.data)
    if (response.data && response.data.code === 'SUCCESS' && response.data.data) {
      const publicKey = response.data.data.publicKey as string
      rsaPublicKeyCache = publicKey
      // console.log('成功获取RSA公钥:', rsaPublicKeyCache)
      return publicKey
    }
    // console.log('RSA公钥响应格式不正确:', response.data)
    throw new Error('获取RSA公钥失败')
  } catch (error) {
    // console.error('获取RSA公钥失败:', error)
    throw error
  }
}

// 创建axios实例
const httpService = axios.create({
  baseURL: '',
  // 请求超时时间
  timeout: 1000 * 60, // 需自定义
   paramsSerializer: (params) =>
    qs.stringify(params, { arrayFormat: 'repeat' })
})


// if(JSON.parse(localStorage.getItem('app'))?.andrioInfo){

//    {version,versionCode,pkg,platform,di,oi,dn} = JSON.parse(localStorage.getItem('app'))?.andrioInfo;
// }

// request拦截器
httpService.interceptors.request.use(
  async (config: any) => {
    const requestTimestamp = Date.now()

    // 添加签名代理标识
    config.headers['sign-agent'] = 'ydppt'

    // Web 端：X-Sign = md5(deviceId + timestamp + SECRET_KEY)
    if (!isMobile()) {
      config.headers['X-Sign'] = getWebXSignHeader(requestTimestamp)
    }

    let token = getToken()
    if (
      !isMiniAppWebview()
      && window.prompt
      && /iPhone|iPad|iPod/i.test(navigator.userAgent)
      && !shouldSkipIOSNativeBridge(location.pathname)
    ) {
      token = window.prompt("ydppt://getToken") || ''
    }
    // token 为空时不传 Authorization，避免 Bearer null 触发登录失效
    if (token) {
      config.headers.Authorization = 'Bearer ' + token
    } else {
      delete config.headers.Authorization
    }

    if (config.method === 'post') {
      const base = await buildCommonPostDataBase(requestTimestamp)
      config.data = {
        ...config.data,
        ...base,
      }
    }

    //针对移动端h5进行接口测试赋值基本参数
        if(localStorage.getItem('token') && isMobile() && import.meta.env.MODE == 'development' && !isMiniAppWebview()){
          config.headers.Authorization = `Bearer ${localStorage.getItem('token')}`;
          config.data = {
            ...config.data,
            "version":"1.0.0",
            "versionInt":100,
            "deviceType":"ANDROID",
            "di":"33814f652fea013d",
            "oi":"f9439abcbee30c78",
            "dn":"2210132C",
            "pkg":"ydppt_official",
            "timestamp":Date.now(),
            "app":"com.gzxyz.ai.ydppt"
          }
        }

    // 加密处理逻辑
    if (config.encrypt === true && config.method === 'post') {
      try {
        // console.log('开始加密请求，原始数据:', config.data)

        // 提取params字段用于加密
        const { params, ...otherFields } = config.data
        // console.log('需要加密的params:', params)
        // console.log('不加密的其他字段:', otherFields)

        // 获取RSA公钥
        const publicKey = await getRSAPublicKey(otherFields.deviceType)

        // 只加密params字段
        const { encryptedBody, encryptedAESKey, rawAESKey } = encryptRequestBody(params, publicKey)

        // console.log('加密后的params:', encryptedBody)
        // console.log('加密后的AES密钥:', encryptedAESKey)

        // 将加密后的params和其他字段组合，并添加ev:2标识
        config.data = {
          ...otherFields,
          params: encryptedBody
        }

        // 将加密的AES密钥和加密版本标识添加到请求头
        config.headers['enc-key'] = encryptedAESKey
        config.headers['ev'] = 2
        
        // 缓存原始AES密钥用于响应解密
        if (otherFields.requestId) {
          cacheDecryptionKey(otherFields.requestId, rawAESKey);
        }

      } catch (error) {
        // console.error('请求加密失败:', error)
        return Promise.reject(new Error('请求加密失败'))
      }
    }

    return config
  },
  (error: any) => {
    // Do something with request error
    // console.log(error) // for debug
    return Promise.reject(error)
  }
)

// respone拦截器
httpService.interceptors.response.use(
  async (response: any) => {
    // 统一处理状态;
    let res: any = response.data  
    
    // 检查响应是否加密(x-encrypted为true表示已加密)
    const xEncrypted: string | undefined = response.headers['x-encrypted'];

    if (xEncrypted === 'true') {
      try {

        // 从请求配置中获取requestId以检索解密密钥
        let requestId: string | undefined;
        try {
          // 尝试从response.config.data中获取requestId
          const requestData = typeof response.config.data === 'string' 
            ? JSON.parse(response.config.data) 
            : response.config.data;
          requestId = requestData?.requestId;
        } catch (e) {
          // 如果解析失败，则直接使用
          requestId = response.config.data?.requestId;
        }
        
    
        
        if (requestId) {
          const decryptionKey = getDecryptionKey(requestId);

          
          if (decryptionKey) {
            // 使用缓存的AES密钥解密响应数据
            const decryptedData = decryptAES(res.data, decryptionKey);
            res['data'] = JSON.parse(decryptedData);
            
            // 清除已使用的解密密钥
            clearDecryptionKey(requestId);
          } else {
            console.warn('未找到解密密钥');
          }
        } else {
          console.warn('未找到RequestId');
        }
      } catch (error) {
        // console.error('响应解密失败:', error);
        ElMessage.error({
          message: '数据解析失败',
          offset: 85
        });
        return Promise.reject(new Error('响应解密失败'));
      }
    }
    
    //AUTHORIZED_EXPIRE: 授权过期  UNAUTHORIZED：未登录 USER_STATUS_ABNORMAL：账号已注销
    if((res.code == 'AUTHORIZED_EXPIRE' || res.code == 'UNAUTHORIZED' || res.code == 'USER_STATUS_ABNORMAL') && !isMobile()){
        const { useUserStore } = await import('@/store/user'); // 动态导入，避免拉入整包store依赖
        const userStore = useUserStore();
       ElMessage.error({
           message: res.msg,
           offset: 85 // 根据需要调整数值
         });
        removeToken()
        userStore.logout()
        // if(window.referToken){
        //   window.referToken()
        // }
        localStorage.removeItem('user')
      // if(response.config.url !== '/api/user/info'){
      //   window.openLogin(true)
      // }

    } else if((res.code == 'AUTHORIZED_EXPIRE' || res.code == 'UNAUTHORIZED' || res.code == 'USER_STATUS_ABNORMAL ') && isMobile()){
      if(response.config.url !== '/api/user/info'){
        // if(window.JsAndroid && JsAndroid.isLogin && !JsAndroid.isLogin()){
        //   JsAndroid.login()
        // } else if(window.prompt) {
        //     const isLogin = window.prompt("ydppt://isLogin")
        //     const isLoggedIn = isLogin !== null && isLogin !== undefined && isLogin !== "false";
        //     if(!isLoggedIn){
        //       window.prompt("ydppt://login")
        //     }
        // }
      }
    } 
    return res
  },
  (error: any) => {
    console.log('err' + error) // for debug
    ElMessage.error({
      message: error.message,
      offset: 85
    });
    return Promise.reject(error)
  }
)

/*
 *  get请求
 *  url:请求地址
 *  params:参数
 * */
export function get(url: string, params: any = {}) {
  return new Promise((resolve, reject) => {
    httpService({
      url: url,
      method: 'get',
      params: params
    })
      .then((response: any) => {
        resolve(response)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

/*
 *  post请求
 *  url:请求地址
 *  params:参数
 * */
export function post(url: string, data: any = {}, config: any = {}) {
  return new Promise((resolve, reject) => {
    httpService({
      url: url,
      method: 'post',
      data: data,
      ...config
    })
      .then((response: any) => {
        resolve(response)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

/*
 *  put请求
 *  url:请求地址
 *  data:参数
 * */
export function put(url: string, data: any = {}, params?: any) {
  return new Promise((resolve, reject) => {
    httpService({
      url: url,
      method: 'put',
      data: data,
      params
    })
      .then((response: any) => {
        resolve(response)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

/*
 *  delete请求
 *  url:请求地址
 *  data:参数
 * */
export function deleteRequest(url: string, params: any = {}, data: any = {}) {
  return new Promise((resolve, reject) => {
    httpService({
      url: url,
      method: 'delete',
      params: params,
      data
    })
      .then((response: any) => {
        resolve(response)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}
/*
 *  文件上传
 *  url:请求地址
 *  data:数据
 *  param:参数
 * */
export function fileUpload(url: string, data: any = {}, param?: any) {
  return new Promise((resolve, reject) => {
    httpService({
      url: url,
      method: 'post',
      params: param, // 参数
      data: data, // 数据
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
      .then((response: any) => {
        resolve(response)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

/*
 *  文件流下载  post请求
 *  url:请求地址
 *  params:数据
 *  param:参数
 * */
export function fileDownload(url: string, params: any) {
  return new Promise((resolve, reject) => {
    httpService({
      url: url,
      method: 'post',
      data: params, // 数据
      responseType: 'blob'
    })
      .then((response: any) => {
        resolve(response)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

/**
 * 文件流下载  get请求
 * 请求地址
 * @param {*} url
 * @param {*} params
 */
export function fileDownloadGet(url: string, params: any) {
  return new Promise((resolve, reject) => {
    httpService({
      url: url,
      method: 'get',
      params: params, // 数据
      responseType: 'blob'
    })
      .then((response: any) => {
        resolve(response)
      })
      .catch((error: any) => {
        reject(error)
      })
  })
}

const http = {
  baseURL,
  fileURL,
  get,
  post,
  put,
  deleteRequest,
  fileUpload,
  fileDownload,
  fileDownloadGet
}

export default http